/**
 * Cookie retrieval result with enhanced type safety
 */
type CookieResult<T extends string> = T extends '' ? null : string | null;

export interface CookieWriteOptions {
  domain?: string;
  maxAgeSeconds: number;
  path?: string;
  sameSite?: 'Lax' | 'None' | 'Strict';
  secure?: boolean;
}

const COOKIE_NAME_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/u;
const COOKIE_DOMAIN_PATTERN = /^\.?[A-Za-z0-9.-]+$/u;

/**
 * Writes a cookie synchronously so callers can verify persistence immediately.
 * This is required for identity and attribution flows where an asynchronous
 * Cookie Store write could race a navigation or outbound request.
 */
export function setCookie(name: string, value: string, options: CookieWriteOptions): boolean {
  if (!COOKIE_NAME_PATTERN.test(name)) throw new TypeError('Cookie name contains invalid characters');
  if (!Number.isFinite(options.maxAgeSeconds)) throw new TypeError('Cookie max age must be finite');
  if (options.domain && !COOKIE_DOMAIN_PATTERN.test(options.domain)) {
    throw new TypeError('Cookie domain contains invalid characters');
  }

  const path = options.path ?? '/';
  const pathHasInvalidCharacter = [...path].some(
    character => character.charCodeAt(0) <= 0x20 || character === ';' || character === ',',
  );
  if (pathHasInvalidCharacter) throw new TypeError('Cookie path contains invalid characters');

  const attributes = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${Math.trunc(options.maxAgeSeconds).toString()}`,
    `Path=${path}`,
    `SameSite=${options.sameSite ?? 'Lax'}`,
  ];
  if (options.domain) attributes.push(`Domain=${options.domain}`);
  if (options.secure) attributes.push('Secure');

  return Reflect.set(document, 'cookie', attributes.join('; '));
}

function splitCookie(cookie: string): { name: string; value: string } | null {
  const separatorIndex = cookie.indexOf('=');
  if (separatorIndex === -1) return null;
  return {
    name: cookie.substring(0, separatorIndex),
    value: cookie.substring(separatorIndex + 1),
  };
}

/**
 * Advanced cookie getter with comprehensive error handling and type safety
 * Handles URL encoding/decoding and malformed cookie scenarios gracefully
 *
 * @template TName - The cookie name type for enhanced IntelliSense
 * @param name - The exact name of the cookie to retrieve
 * @returns Decoded cookie value or null if not found/invalid
 *
 * @example
 * ```typescript
 * const sessionId = getCookie('sessionId'); // string | null
 * const userPrefs = getCookie('preferences'); // string | null
 * ```
 */
export const getCookie = <TName extends string>(name: TName): CookieResult<TName> => {
  if (document.cookie === '') {
    // Handle no cookies case upfront
    return null as CookieResult<TName>;
  }

  for (const rawCookie of document.cookie.split(';')) {
    const cookie = splitCookie(rawCookie.trimStart());
    if (cookie?.name === name) {
      // Decode the value, as cookie values are often URL-encoded
      try {
        return decodeURIComponent(cookie.value) as CookieResult<TName>;
      } catch {
        // Handle cases where decodeURIComponent might fail (e.g., malformed URI sequence)
        return cookie.value as CookieResult<TName>; // Return raw value on decode error
      }
    }
  }
  return null as CookieResult<TName>;
};
